import React from 'react';
import { QueryKeys, useCreditExplorerProductsQuery } from '../../shared/hooks';
import { Deal, ProductSubscription } from '../../shared/models';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../shared/components';
import { Formik, FormikErrors } from 'formik';
import { EnumUtils, FormikUtils, NumberUtils } from '../../shared/utils';
import { EmptyGuid } from '../../shared/constants';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { Skeleton, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Grid } from '@mui/material';
import { SuccessFeeTierTableDisplay } from './SuccessFeeTierTableDisplay';
import { getApiResponseData } from '../../shared/utils/axios';

interface CreateSubscriptionDialogProps {
	open: boolean;
	onClose: () => void;
	deal: Deal;
}

export const CreateSubscriptionDrawer: React.FC<CreateSubscriptionDialogProps> = ({ open, onClose, deal }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);
	const productsQuery = useCreditExplorerProductsQuery({
		enabled: open,
		retry: false,
	});

	var validateForm = (values: CreateSubscriptionFormValues): CreateSubscriptionFormErrors => {
		const errors: CreateSubscriptionFormErrors = {};

		if (values.productGuid == EmptyGuid) {
			errors.productGuid = 'A product must be selected.';
		}

		return errors;
	};

	const createSubscriptionMutation = useMutation(
		async ({ values }: CreateSubscriptionMutationVariables) => {
			let formData = new FormData();
			formData.append('productGuid', values.productGuid);

			const { data, error } = await getApiResponseData<ProductSubscription>(
				api.post(CerebroApiEndpoints.Deals.Subscriptions(deal.guid), formData)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(deal.guid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(deal.guid));
				snackbar.enqueueSnackbar('Deal engagement successfully created.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading>Select Product for Deal Engagement</CerebroHeading>

			<Formik
				initialValues={{
					productGuid: EmptyGuid,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(createSubscriptionMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						{productsQuery.isLoading && (
							<>
								<Skeleton height={60} />
								<Skeleton height={60} />
								<Skeleton height={60} />
							</>
						)}
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								{productsQuery.isSuccess && (
									<TableContainer>
										<Table>
											<TableHead>
												<TableRow>
													<TableCell>Name</TableCell>
													<TableCell>Platform Access</TableCell>
													<TableCell>Success Fee</TableCell>
													<TableCell></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{productsQuery.data
													.filter(product => !product.isCompanySubscriptionDealProduct)
													.map(product => {
														return (
															<TableRow key={product.productGuid}>
																<TableCell>{product.productName}</TableCell>
																<TableCell>
																	<CerebroText>
																		{NumberUtils.FormatCurrency(product.setupFee)}{' '}
																		{EnumUtils.GetSubscriptionFrequencyDescription(
																			product.subscriptionFrequency
																		)}
																	</CerebroText>
																</TableCell>
																<TableCell>
																	{product.successFeeType == 'Tiered' &&
																	product.successFeeTiers &&
																	product.successFeeTiers.length > 0 ? (
																		<SuccessFeeTierTableDisplay
																			tiers={product.successFeeTiers}
																		/>
																	) : product.successFeeType == 'Flat' ? (
																		<>{product.basisPoints} bps</>
																	) : (
																		<>--</>
																	)}

																	{product.minimumSuccessFee &&
																	product.minimumSuccessFee > 0 ? (
																		<CerebroText>
																			<strong>Minimum Success Fee: </strong>{' '}
																			{NumberUtils.FormatCurrency(
																				product.minimumSuccessFee
																			)}{' '}
																		</CerebroText>
																	) : (
																		<></>
																	)}
																</TableCell>
																<TableCell>
																	{values.productGuid == product.productGuid ? (
																		<CerebroText>Selected</CerebroText>
																	) : (
																		<CerebroSecondaryButton
																			onClick={() => {
																				setFieldValue(
																					'productGuid',
																					product.productGuid
																				);
																			}}
																		>
																			Select
																		</CerebroSecondaryButton>
																	)}
																</TableCell>
															</TableRow>
														);
													})}
											</TableBody>
										</Table>
									</TableContainer>
								)}
								{touched.productGuid && errors.productGuid != null && (
									<CerebroText variant="error">{errors.productGuid}</CerebroText>
								)}
							</Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type CreateSubscriptionFormErrors = FormikErrors<CreateSubscriptionFormValues>;
type CreateSubscriptionMutationVariables = FormikUtils.MutateOnSubmitVariables<CreateSubscriptionFormValues>;
interface CreateSubscriptionFormValues {
	productGuid: string;
}
